import { createContext, use, useCallback, useRef, type ReactNode } from "react";
import { toast } from "sonner";
import {
  Toast,
  ToastProgressBar,
  TOAST_CLASSNAMES,
  TOAST_DESCRIPTION_PROGRESS_CLASS,
} from "@/components/molecules/toast";
import {
  TOAST_SUCCESS,
  TOAST_ERROR,
  TOAST_SAVING,
  TOAST_DISCONNECTED,
  TOAST_RECONNECTED,
  RECONNECTING_TOAST_ID,
  ERROR_TOAST_ID,
  RECONNECTED_TOAST_ID,
  UNKNOWN_SPARX_EDITOR_TOAST_ID,
} from "@/constants/toastCopy";

const SUCCESS_DURATION_MS = 5000;
const RECONNECTING_THROTTLE_MS = 45_000;
/** Delay before dismissing the previous toast so the new toast's slide-in animation is visible. */
const TOAST_DISMISS_PREVIOUS_DELAY_MS = 200;

export interface ToastContextValue {
  showSuccess: () => void;
  showError: () => void;
  /** Same styling/duration as `showError`, with a custom warning message (e.g. unknown sparx id). */
  showWarningMessage: (message: string) => void;
  showReconnecting: () => void;
  dismissReconnecting: () => void;
  /** Shows a toast that stays until the promise settles (loading → success or error). Use for sparx editor save. */
  showSavePromise: <T>(
    promise: Promise<T>,
    options?: { loading?: string; success?: string; error?: string },
  ) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

type ToastClosePayload = { id?: string | number };

const successToastClasses = `${TOAST_CLASSNAMES.base} pb-0`;
const warningToastClasses = `${TOAST_CLASSNAMES.base} pb-0`;

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const lastReconnectingShownAtRef = useRef(0);
  const reconnectingVisibleRef = useRef(false);
  const currentToastMessageRef = useRef<string | null>(null);
  const lastToastIdRef = useRef<string | number | null>(null);

  const handleToastClose = useCallback((t: ToastClosePayload) => {
    if (t.id != null && lastToastIdRef.current === t.id) {
      lastToastIdRef.current = null;
      currentToastMessageRef.current = null;
    }
  }, []);

  const dismissToastById = useCallback((id: string | number) => {
    toast.dismiss(id);
  }, []);

  /** Dismiss the previous toast after a short delay so the new toast's slide-in animation plays first. */
  const dismissPreviousAfterNewToast = useCallback(
    (previousId: string | number | null) => {
      if (previousId == null) return;
      setTimeout(
        () => dismissToastById(previousId),
        TOAST_DISMISS_PREVIOUS_DELAY_MS,
      );
    },
    [dismissToastById],
  );

  /** Show new toast first, then dismiss the previous by id so the previous toast exits from the correct position (below the new one). */
  const showSuccess = useCallback(() => {
    if (currentToastMessageRef.current === TOAST_SUCCESS) {
      if (lastToastIdRef.current != null) {
        toast.success(TOAST_SUCCESS, {
          id: lastToastIdRef.current,
          duration: SUCCESS_DURATION_MS,
          description: <ToastProgressBar duration={SUCCESS_DURATION_MS} />,
          classNames: {
            toast: successToastClasses,
            description: TOAST_DESCRIPTION_PROGRESS_CLASS,
          },
          onDismiss: handleToastClose,
          onAutoClose: handleToastClose,
        });
      }
      return;
    }
    const previousId = lastToastIdRef.current;
    currentToastMessageRef.current = TOAST_SUCCESS;
    const id = toast.success(TOAST_SUCCESS, {
      duration: SUCCESS_DURATION_MS,
      description: <ToastProgressBar duration={SUCCESS_DURATION_MS} />,
      classNames: {
        toast: successToastClasses,
        description: TOAST_DESCRIPTION_PROGRESS_CLASS,
      },
      onDismiss: handleToastClose,
      onAutoClose: handleToastClose,
    });
    lastToastIdRef.current = id;
    dismissPreviousAfterNewToast(previousId);
  }, [dismissPreviousAfterNewToast, handleToastClose]);

  const showError = useCallback(() => {
    if (currentToastMessageRef.current === TOAST_ERROR) {
      if (lastToastIdRef.current != null) {
        toast.warning(TOAST_ERROR, {
          id: ERROR_TOAST_ID,
          duration: SUCCESS_DURATION_MS,
          description: <ToastProgressBar duration={SUCCESS_DURATION_MS} />,
          classNames: {
            toast: warningToastClasses,
            description: TOAST_DESCRIPTION_PROGRESS_CLASS,
          },
          onDismiss: handleToastClose,
          onAutoClose: handleToastClose,
        });
      }
      return;
    }
    const previousId = lastToastIdRef.current;
    currentToastMessageRef.current = TOAST_ERROR;
    toast.warning(TOAST_ERROR, {
      id: ERROR_TOAST_ID,
      duration: SUCCESS_DURATION_MS,
      description: <ToastProgressBar duration={SUCCESS_DURATION_MS} />,
      classNames: {
        toast: warningToastClasses,
        description: TOAST_DESCRIPTION_PROGRESS_CLASS,
      },
      onDismiss: handleToastClose,
      onAutoClose: handleToastClose,
    });
    lastToastIdRef.current = ERROR_TOAST_ID;
    dismissPreviousAfterNewToast(previousId);
  }, [dismissPreviousAfterNewToast, handleToastClose]);

  const showWarningMessage = useCallback(
    (message: string) => {
      const progressBar = <ToastProgressBar duration={SUCCESS_DURATION_MS} />;
      toast.warning(message, {
        id: UNKNOWN_SPARX_EDITOR_TOAST_ID,
        duration: SUCCESS_DURATION_MS,
        description: progressBar,
        classNames: {
          toast: warningToastClasses,
          description: TOAST_DESCRIPTION_PROGRESS_CLASS,
        },
        onDismiss: handleToastClose,
        onAutoClose: handleToastClose,
      });
      lastToastIdRef.current = UNKNOWN_SPARX_EDITOR_TOAST_ID;
    },
    [handleToastClose],
  );

  const showReconnecting = useCallback(() => {
    if (currentToastMessageRef.current === TOAST_DISCONNECTED) return;
    const now = Date.now();
    if (now - lastReconnectingShownAtRef.current < RECONNECTING_THROTTLE_MS)
      return;
    lastReconnectingShownAtRef.current = now;
    reconnectingVisibleRef.current = true;
    const previousId = lastToastIdRef.current;
    currentToastMessageRef.current = TOAST_DISCONNECTED;
    toast.info(TOAST_DISCONNECTED, {
      id: RECONNECTING_TOAST_ID,
      duration: Number.POSITIVE_INFINITY,
      closeButton: false,
      dismissible: false,
      onDismiss: handleToastClose,
      onAutoClose: handleToastClose,
    });
    lastToastIdRef.current = RECONNECTING_TOAST_ID;
    dismissPreviousAfterNewToast(previousId);
  }, [dismissPreviousAfterNewToast, handleToastClose]);

  const dismissReconnecting = useCallback(() => {
    if (!reconnectingVisibleRef.current) {
      currentToastMessageRef.current = null;
      return;
    }
    reconnectingVisibleRef.current = false;
    lastReconnectingShownAtRef.current = 0;
    const previousId = lastToastIdRef.current;
    currentToastMessageRef.current = TOAST_RECONNECTED;
    toast.success(TOAST_RECONNECTED, {
      id: RECONNECTED_TOAST_ID,
      duration: SUCCESS_DURATION_MS,
      description: <ToastProgressBar duration={SUCCESS_DURATION_MS} />,
      classNames: {
        toast: successToastClasses,
        description: TOAST_DESCRIPTION_PROGRESS_CLASS,
      },
      onDismiss: handleToastClose,
      onAutoClose: handleToastClose,
    });
    lastToastIdRef.current = RECONNECTED_TOAST_ID;
    dismissPreviousAfterNewToast(previousId);
  }, [dismissPreviousAfterNewToast, handleToastClose]);

  const showSavePromise = useCallback(
    <T,>(
      promise: Promise<T>,
      options?: { loading?: string; success?: string; error?: string },
    ) => {
      const loadingId = toast.loading(options?.loading ?? TOAST_SAVING, {
        duration: Number.POSITIVE_INFINITY,
        closeButton: false,
      });
      const progressBar = <ToastProgressBar duration={SUCCESS_DURATION_MS} />;
      const successMessage = options?.success ?? TOAST_SUCCESS;
      const errorMessage = options?.error ?? TOAST_ERROR;
      promise
        .then(() => {
          toast.dismiss(loadingId);
          if (currentToastMessageRef.current === successMessage) {
            if (lastToastIdRef.current != null) {
              toast.success(successMessage, {
                id: lastToastIdRef.current,
                duration: SUCCESS_DURATION_MS,
                classNames: {
                  toast: successToastClasses,
                  description: TOAST_DESCRIPTION_PROGRESS_CLASS,
                },
                description: progressBar,
                onDismiss: handleToastClose,
                onAutoClose: handleToastClose,
              });
            }
            return;
          }
          const previousId = lastToastIdRef.current;
          currentToastMessageRef.current = successMessage;
          const id = toast.success(successMessage, {
            duration: SUCCESS_DURATION_MS,
            classNames: {
              toast: successToastClasses,
              description: TOAST_DESCRIPTION_PROGRESS_CLASS,
            },
            description: progressBar,
            onDismiss: handleToastClose,
            onAutoClose: handleToastClose,
          });
          lastToastIdRef.current = id;
          dismissPreviousAfterNewToast(previousId);
        })
        .catch(() => {
          toast.dismiss(loadingId);
          if (currentToastMessageRef.current === errorMessage) {
            if (lastToastIdRef.current != null) {
              toast.warning(errorMessage, {
                id: lastToastIdRef.current,
                duration: SUCCESS_DURATION_MS,
                classNames: {
                  toast: warningToastClasses,
                  description: TOAST_DESCRIPTION_PROGRESS_CLASS,
                },
                description: progressBar,
                onDismiss: handleToastClose,
                onAutoClose: handleToastClose,
              });
            }
            return;
          }
          const previousId = lastToastIdRef.current;
          currentToastMessageRef.current = errorMessage;
          const id = toast.warning(errorMessage, {
            duration: SUCCESS_DURATION_MS,
            classNames: {
              toast: warningToastClasses,
              description: TOAST_DESCRIPTION_PROGRESS_CLASS,
            },
            description: progressBar,
            onDismiss: handleToastClose,
            onAutoClose: handleToastClose,
          });
          lastToastIdRef.current = id;
          dismissPreviousAfterNewToast(previousId);
        });
    },
    [dismissPreviousAfterNewToast, handleToastClose],
  );

  const value: ToastContextValue = {
    showSuccess,
    showError,
    showWarningMessage,
    showReconnecting,
    dismissReconnecting,
    showSavePromise,
  };

  return (
    <ToastContext value={value}>
      {children}
      <Toast />
    </ToastContext>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = use(ToastContext);
  if (ctx == null) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return ctx;
};
